/**
 * Keep speaker-camera frames fresh for canvas compositing.
 *
 * Drawing a &lt;video&gt; in a background Twinnee tab (typical while presenting)
 * often freezes or drops to ~1fps, so the PiP face lags in the recording even
 * though the shared screen keeps moving. ImageCapture.grabFrame() reads the
 * live MediaStreamTrack directly and stays timely in hidden tabs.
 */

/**
 * @param {MediaStreamTrack | null | undefined} track
 * @param {HTMLVideoElement | null | undefined} videoEl
 * @returns {{
 *   getSource: () => CanvasImageSource | null,
 *   kick: () => void,
 *   stop: () => void,
 * }}
 */
export function createSpeakerFramePump(track, videoEl) {
  let stopped = false;
  let inflight = false;
  let latest = null;
  let imageCapture = null;
  let rvfcHandle = 0;
  let fallbackTimer = 0;

  if (track && track.readyState === 'live') {
    try {
      track.contentHint = 'motion';
    } catch {
      /* ignore */
    }
    try {
      if (typeof ImageCapture !== 'undefined') {
        imageCapture = new ImageCapture(track);
      }
    } catch {
      imageCapture = null;
    }
  }

  const adopt = (bmp) => {
    if (stopped) {
      try {
        bmp?.close?.();
      } catch {
        /* ignore */
      }
      return;
    }
    if (latest && latest !== bmp) {
      try {
        latest.close?.();
      } catch {
        /* ignore */
      }
    }
    latest = bmp;
  };

  const grab = () => {
    if (stopped || inflight) return;
    inflight = true;
    const finish = () => {
      inflight = false;
    };

    if (imageCapture) {
      imageCapture
        .grabFrame()
        .then((bmp) => {
          adopt(bmp);
        })
        .catch(() => {
          // Transient failures (track restart) — keep trying; video fallback still works.
        })
        .finally(finish);
      return;
    }

    if (videoEl && videoEl.readyState >= 2 && (videoEl.videoWidth || 0) > 0) {
      const maybeBitmap = typeof createImageBitmap === 'function'
        ? createImageBitmap(videoEl)
        : null;
      if (maybeBitmap) {
        maybeBitmap
          .then((bmp) => adopt(bmp))
          .catch(() => {})
          .finally(finish);
        return;
      }
    }
    finish();
  };

  const scheduleRvfc = () => {
    if (stopped || !videoEl || typeof videoEl.requestVideoFrameCallback !== 'function') return;
    rvfcHandle = videoEl.requestVideoFrameCallback(() => {
      grab();
      scheduleRvfc();
    });
  };

  scheduleRvfc();
  // Kick immediately so the first canvas frames aren't empty.
  grab();
  // Steady pump for hidden tabs (rvfc often pauses when the document is hidden).
  fallbackTimer = setInterval(grab, 33);

  return {
    getSource: () => {
      if (stopped) return null;
      if (latest) return latest;
      if (videoEl && videoEl.readyState >= 2 && (videoEl.videoWidth || 0) > 0) return videoEl;
      return null;
    },
    kick: grab,
    stop: () => {
      stopped = true;
      if (fallbackTimer) clearInterval(fallbackTimer);
      fallbackTimer = 0;
      if (rvfcHandle && videoEl && typeof videoEl.cancelVideoFrameCallback === 'function') {
        try {
          videoEl.cancelVideoFrameCallback(rvfcHandle);
        } catch {
          /* ignore */
        }
      }
      rvfcHandle = 0;
      imageCapture = null;
      if (latest) {
        try {
          latest.close?.();
        } catch {
          /* ignore */
        }
      }
      latest = null;
    },
  };
}
